import { readRuntimeConfig } from './runtime-config';

type GroupsConfig = Record<string, { features: string[] }>;

const getEnv = (key: string, fallback = '') => process.env[`NEXT_PUBLIC_${key}`] ?? fallback;

const parseBool = (value: string | undefined, fallback = false) =>
  value === undefined ? fallback : value === 'true';

const parseJSON = <T>(key: string, value: string | undefined, fallback: T): T => {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.error(`Failed to parse JSON env for ${key}:`, error);
    return fallback;
  }
};

export const getApiEndpoint = (): string => {
  const envEndpoint = getEnv('API_ENDPOINT');
  if (envEndpoint) return envEndpoint;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`;
  }
  return '';
};

export const appConfig = {
  ROLE_ARN: getEnv('ROLE_ARN'),
  REGION: getEnv('REGION', 'us-east-1'),
  API_ENDPOINT: getEnv('API_ENDPOINT'),
  USER_POOL_ID: getEnv('USER_POOL_ID'),
  CLIENT_ID: getEnv('CLIENT_ID'),
  CLIENT_NAME: getEnv('CLIENT_NAME'),
  OUTPUTS_BUCKET_NAME: getEnv('OUTPUTS_BUCKET_NAME'),
  DATA_BUCKET: getEnv('DATA_BUCKET'),
  PROVISION_Q_RESOURCES: parseBool(process.env.NEXT_PUBLIC_PROVISION_Q_RESOURCES, false),
  PREFERRED_KNOWLEDGE_BASE: getEnv('PREFERRED_KNOWLEDGE_BASE'),
  BEDROCK_KNOWLEDGE_BASE_ID: getEnv('BEDROCK_KNOWLEDGE_BASE_ID'),
  Q_APPLICATION_ID: getEnv('Q_APPLICATION_ID'),
  Q_INDEX_ID: getEnv('Q_INDEX_ID'),
  Q_RETRIEVER_ID: getEnv('Q_RETRIEVER_ID'),
  BEDROCK_ACCOUNT: getEnv('BEDROCK_ACCOUNT'),
  PIPEDREAM_RELAY_LAMBDA_ARN: getEnv('PIPEDREAM_RELAY_LAMBDA_ARN'),
  PIPEDREAM_INTEGRATIONS: parseJSON<Record<string, unknown>>(
    'PIPEDREAM_INTEGRATIONS',
    process.env.NEXT_PUBLIC_PIPEDREAM_INTEGRATIONS,
    {},
  ),
  BRANDING_PROVIDER_ENABLED: parseBool(process.env.NEXT_PUBLIC_BRANDING_PROVIDER_ENABLED, false),
  // Runtime-resolved via getters, NOT build-time inlined. These two differ per environment
  // (they are staging-only preview surfaces), so baking them into the image is exactly what
  // stopped one image being promotable across the ladder. A getter re-reads the bridge on
  // every access, so existing `appConfig.RULES_PREVIEW_ENABLED` call sites need no change.
  // See config/runtime-config.ts.
  get RULES_PREVIEW_ENABLED(): boolean {
    return readRuntimeConfig().rulesPreviewEnabled;
  },
  get COST_PREVIEW_ENABLED(): boolean {
    return readRuntimeConfig().costPreviewEnabled;
  },
  AGENTS: parseJSON<unknown[]>('AGENTS', process.env.NEXT_PUBLIC_AGENTS, []),
  BRANDING_API_BASE_URL: getEnv('BRANDING_API_BASE_URL'),
  BRANDING_ASSETS_BUCKET: getEnv('BRANDING_ASSETS_BUCKET'),
  BRANDING_ASSETS_PREFIX: getEnv('BRANDING_ASSETS_PREFIX'),
  GROUPS: parseJSON<GroupsConfig>('GROUPS', process.env.NEXT_PUBLIC_GROUPS, {}),
};

export type { GroupsConfig };
